import {
  EntityObject,
  getObjectId,
  getObjectType,
  TagAssignment,
  TagAssignmentBulkRequest,
  useAddTagAssignmentToObjectMutation,
  useBulkSaveTagAssignmentsMutation,
  useCreateTagAssignmentMutation,
  useDeleteTagAssignmentMutation,
  useRemoveTagAssignmentFromObjectMutation,
  useReplaceTagAssignmentsForObjectMutation,
  useUpdateTagAssignmentMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseTagAssignmentMutationProps = {}

export type UseTagAssignmentMutationResult = {
  createTagAssignment: (tagAssignment: Partial<TagAssignment>, callback: EntityMutationCallback<TagAssignment> | undefined) => void
  updateTagAssignment: (tagAssignment: Partial<TagAssignment>, callback: EntityMutationCallback<TagAssignment> | undefined) => void
  deleteTagAssignment: (tagAssignmentId: string, callback: EntityMutationCallback | undefined) => void

  replaceTagAssignments: (tagDefIds: string[], object: EntityObject, callback: EntityMutationCallback<TagAssignment[]> | undefined) => void
  addTagAssignment: (tagDefId: string, object: EntityObject, callback: EntityMutationCallback<TagAssignment> | undefined) => void
  removeTagAssignment: (tagDefId: string, object: EntityObject, callback: EntityMutationCallback | undefined) => void

  bulkSaveTagAssignments: (request: TagAssignmentBulkRequest, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useTagAssignmentMutation = (props: UseTagAssignmentMutationProps = {}): UseTagAssignmentMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createTagAssignment, {isLoading: isCreating}] = useCreateTagAssignmentMutation()
  const [_updateTagAssignment, {isLoading: isUpdating}] = useUpdateTagAssignmentMutation()
  const [_deleteTagAssignment, {isLoading: isDeleting}] = useDeleteTagAssignmentMutation()
  const [_replaceTagAssignments, {isLoading: isReplacing}] = useReplaceTagAssignmentsForObjectMutation()
  const [_addTagAssignment, {isLoading: isAdding}] = useAddTagAssignmentToObjectMutation()
  const [_removeTagAssignment, {isLoading: isRemoving}] = useRemoveTagAssignmentFromObjectMutation()
  const [_bulkSaveTagAssignments, {isLoading: isBulkSaving}] = useBulkSaveTagAssignmentsMutation()

  useEffect(() => {
    _setCreating(isCreating || isAdding)
  }, [isCreating, isAdding, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating || isReplacing || isBulkSaving,)
  }, [isUpdating, isReplacing, isBulkSaving, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting || isRemoving)
  }, [isDeleting, isRemoving, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useTagAssignmentUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.tagassignment", "Tag Assignment"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagAssignmentUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useTagAssignmentUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.tagassignment", "Tag Assignment"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagAssignmentUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useTagAssignmentUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.tagassignment", "Tag Assignment"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagAssignmentUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<TagAssignment>, callback: EntityMutationCallback<TagAssignment> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagassignment", "Tag Assignment") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<TagAssignment>, callback: EntityMutationCallback<TagAssignment> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagassignment", "Tag Assignment") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleReplaceResult = useCallback((promise: Promise<TagAssignment[]>, callback: EntityMutationCallback<TagAssignment[]> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagassignment.plural", "Tag Assignments") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleBulkSaveResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagassignment.plural", "Tag Assignments") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagassignment", "Tag Assignment") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((tagAssignmentId: string, callback: EntityMutationCallback<TagAssignment> | undefined) => {
    const promise = _deleteTagAssignment({id: tagAssignmentId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteTagAssignment, handleDeleteResult])

  const createTagAssignment = useCallback((tagAssignment: Partial<TagAssignment>, callback: EntityMutationCallback<TagAssignment> | undefined) => {
    const promise = _createTagAssignment(tagAssignment).unwrap()
    handleCreateResult(promise, callback)
  }, [_createTagAssignment, handleCreateResult])

  const updateTagAssignment = useCallback((tagAssignment: Partial<TagAssignment>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateTagAssignment(tagAssignment).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateTagAssignment, handleUpdateResult])

  const deleteTagAssignment = useCallback((tagAssignmentId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.tagassignment", "Tag Assignment"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.tagassignment.confirm", "Are you sure you want to delete this tag assignment?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(tagAssignmentId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const replaceTagAssignments = useCallback((tagDefIds: string[], object: EntityObject, callback: EntityMutationCallback<TagAssignment[]> | undefined) => {
    const promise = _replaceTagAssignments({
      tagDefIds,
      object: {
        id: getObjectId(object),
        type: getObjectType(object)
      }
    }).unwrap()
    handleReplaceResult(promise, callback)
  }, [_replaceTagAssignments, handleReplaceResult])

  const addTagAssignment = useCallback((tagDefId: string, object: EntityObject, callback: EntityMutationCallback<TagAssignment> | undefined) => {
    const promise = _addTagAssignment({
      tagDefId,
      object: {
        id: getObjectId(object),
        type: getObjectType(object)
      }
    }).unwrap()
    handleCreateResult(promise, callback)
  }, [_addTagAssignment, handleCreateResult])

  const removeTagAssignment = useCallback((tagDefId: string, object: EntityObject, callback: EntityMutationCallback | undefined) => {
    const promise = _removeTagAssignment({
      tagDefId,
      object: {
        id: getObjectId(object),
        type: getObjectType(object)
      }
    }).unwrap()
    handleDeleteResult(promise, callback)
  }, [_removeTagAssignment, handleDeleteResult])

  const bulkSaveTagAssignments = useCallback((request: TagAssignmentBulkRequest, callback: EntityMutationCallback | undefined) => {
    const promise = _bulkSaveTagAssignments(request).unwrap()
    handleBulkSaveResult(promise, callback)
  }, [_bulkSaveTagAssignments, handleBulkSaveResult])

  return {
    createTagAssignment: createTagAssignment,
    updateTagAssignment: updateTagAssignment,
    deleteTagAssignment: deleteTagAssignment,
    replaceTagAssignments: replaceTagAssignments,
    addTagAssignment: addTagAssignment,
    removeTagAssignment: removeTagAssignment,
    bulkSaveTagAssignments: bulkSaveTagAssignments,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
